/**
 * Hygraph image transformations.
 *
 * Hygraph exposes the same transform pipeline two ways: as the `transformation`
 * argument on the GraphQL `url` field, and as path segments on the asset URL
 * (`.../resize=width:720/quality=value:70/output=format:webp/<handle>` —
 * literally what the GraphQL form returns). We build the path form here
 * because a responsive `srcset` needs several widths per asset and the usable
 * widths depend on that asset's own dimensions; the GraphQL form would need a
 * hand-written alias per width per image field.
 *
 * Blog queries must therefore select `width height size mimeType` next to
 * `url` on every asset that goes through here.
 *
 * WHY THE GUARDS: Hygraph's re-encoder is less efficient per pixel than
 * whatever produced these source webps, so a shallow downscale can come back
 * HEAVIER than the original. Measured on production assets (q70 webp):
 *
 *   1536x1024  117 KB  ->  380w   39 KB   (win)
 *   3230x2160  188 KB  ->  912w   67 KB   (win)
 *   1224x817   203 KB  ->  912w  131 KB   (win)
 *   1920x1281  166 KB  ->  912w  220 KB   (LOSS — only 0.07 B/px to begin with)
 *   600x600     64 KB  ->  449w   62 KB   (wash)
 *    420x96    5.8 KB  ->  280w  7.0 KB   (LOSS)
 *
 * So a candidate is only offered when it discards enough pixels to cover the
 * re-encode penalty, and the bar is raised for sources that are already lean.
 * AVIF is deliberately absent: Hygraph currently rejects webp -> avif
 * ("Unsupported transformation ... temporarily disabled") and every asset in
 * this project is already webp, so there is no format win left to take.
 */

// https://<region>.graphassets.com/<environment>/<handle> — exactly two path
// segments. A URL that already carries transform segments will not match and
// is handed back untouched.
const ASSET_URL_RE = /^(https:\/\/[a-z0-9.-]+\.graphassets\.com\/[A-Za-z0-9_-]+)\/([A-Za-z0-9_-]+)$/;

const DEFAULT_QUALITY = 70;

// Formats a raster resize would destroy rather than shrink.
const NON_RASTER_MIME = ["image/svg+xml", "image/gif"];

// Bytes per source pixel. Below this the source is compressed harder than the
// re-encoder manages, so only a deep downscale is worth taking.
const LEAN_BPP = 0.08;

// Share of the source width a candidate may not exceed (~1.3x downscale for a
// normal source, ~2.6x for a lean one).
const MAX_WIDTH_RATIO = 0.77;
const MAX_WIDTH_RATIO_LEAN = 0.38;

function transformable(asset) {
  const url = asset && asset.url;
  if (!url) return null;
  if (asset.mimeType && NON_RASTER_MIME.indexOf(asset.mimeType) !== -1) return null;
  const match = ASSET_URL_RE.exec(url);
  return match ? { base: match[1], handle: match[2] } : null;
}

/**
 * Transformed URL for a single width, or the untouched `url` when the asset
 * cannot be transformed. Height is left off so the source aspect ratio is
 * preserved (Hygraph's default `fit:clip`), making every candidate a drop-in
 * for the original under the layout's `object-fit`/`aspect-ratio` cropping.
 *
 * `fit:clip` is deliberately NOT spelled out: `resize=fit:clip,width:N` needs
 * a comma, and a comma is how a `srcset` attribute separates candidates. The
 * HTML parser does tolerate it (a candidate URL runs to the next whitespace,
 * not the next comma), but nothing downstream — minifiers, proxies, a hand
 * edit — has to. `resize=width:N` is byte-for-byte the same image.
 */
export function hygraphImageUrl(asset, width, options = {}) {
  const parts = transformable(asset);
  if (!parts || !width) return (asset && asset.url) || null;

  return [
    parts.base,
    `resize=width:${Math.round(width)}`,
    `quality=value:${options.quality || DEFAULT_QUALITY}`,
    "output=format:webp",
    parts.handle,
  ].join("/");
}

// Widest candidate worth generating for this asset (0 = generate none).
function candidateLimit(asset) {
  if (!asset.width || !asset.height) return 0;
  const bpp = asset.size ? asset.size / (asset.width * asset.height) : null;
  const ratio = bpp !== null && bpp < LEAN_BPP ? MAX_WIDTH_RATIO_LEAN : MAX_WIDTH_RATIO;
  return Math.floor(asset.width * ratio);
}

/**
 * `srcset` for an asset across a layout's width ladder, or null when nothing
 * is worth offering (caller then just renders `src`).
 *
 * The untouched original always closes the set at its intrinsic width, so a
 * high-DPR viewport can still pick it and is never served more bytes than it
 * downloads today.
 */
export function hygraphSrcset(asset, widths, options = {}) {
  if (!transformable(asset)) return null;

  const limit = candidateLimit(asset);
  const candidates = widths
    .filter((w) => w <= limit)
    .sort((a, b) => a - b)
    .map((w) => `${hygraphImageUrl(asset, w, options)} ${w}w`);

  if (!candidates.length) return null;

  candidates.push(`${asset.url} ${asset.width}w`);
  return candidates.join(", ");
}
